import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Box,
  IconButton,
  CircularProgress,
  TablePagination
} from "@mui/material";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteIcon from "@mui/icons-material/Delete";
import "./voiceMail.css";

const MailboxDetails = ({ mailbox, messages = [], loading = false, onBack }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loadingRowId, setLoadingRowId] = useState(null);
  const [playingRowId, setPlayingRowId] = useState(null);
  const [audio, setAudio] = useState(null);
  const [playingAudioData, setPlayingAudioData] = useState(null);
  const [localMessages, setLocalMessages] = useState(messages);
  const [deletingRowId, setDeletingRowId] = useState(null);

  useEffect(() => {
    setLocalMessages(messages);
  }, [messages]);

  // ✅ Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setPlayingRowId(null); // Close any open audio player when switching pages
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ✅ Slice data for the current view
  const paginatedMessages = localMessages.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handlePlayAudio = async (msg) => {
    try {
      console.log("Playing audio for message ID:", msg);
      if (!msg.presignedUrl) {
        console.error("Presigned URL missing");
        return;
      }

      // Stop previous audio if playing
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }

      setLoadingRowId(msg.id);

      setPlayingRowId(msg.id);

      setPlayingAudioData({
        url: msg.presignedUrl,
        messageId: msg.id,
      });

    } catch (error) {
      console.error("Error playing audio:", error);
    } finally {
      setLoadingRowId(null);
    }
  };
  const handleCloseAudio = () => {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setAudio(null);
    }

    setPlayingRowId(null);
    setPlayingAudioData(null);
  };
  const handleDeleteAudio = async (msg) => {
    try {
      setDeletingRowId(msg.id);
      const payload = {
        Name: msg.audioUrl,
        Action: "Delete"
      };
      const response = await axiosInstance.post(
        endpoints.voiceMailsDisplay.voiceMailAudioList,
        payload, {
        headers: {
          "Content-Type": "application/json",
        },
      }
      );

      setLocalMessages((prev) =>
        prev.filter((item) => item.id !== msg.id)
      );

      if (playingRowId === msg.id) {
        handleCloseAudio();
      }

      mailbox && console.log("Deleted:", msg.audioUrl);

    } catch (error) {
      console.error("Error deleting voicemail:", error);
    } finally {
      setDeletingRowId(null);
    }
  };
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>

      {/* ================= HEADER ================= */}
      <Box
        display="flex"
        alignItems="center"
        px={3}
        py={2}
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f8f9fa"
        }}
      >
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>

        <Box ml={2}>
          <Typography variant="h6" fontWeight={600}>
            {mailbox?.name || "Voicemail"}
          </Typography>
          {/* <Typography variant="body2" color="text.secondary">
            {mailbox?.id || "Mailbox ID"}
          </Typography> */}
        </Box>
      </Box>

      {/* ================= CONDITIONAL CONTENT ================= */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
      ) : localMessages.length === 0 ? (
        <Box p={3}><Typography>No voicemails found.</Typography></Box>
      ) : (
        <>
          <Box p={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell><strong>From</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedMessages.map((msg) => {
                  const isPlaying = playingRowId === msg.id;
                  const isLoading = loadingRowId === msg.id;

                  return (
                    <React.Fragment key={msg.id}>
                      <TableRow hover>
                        <TableCell>{msg.created}</TableCell>
                        <TableCell>{msg.from}</TableCell>

                        {/* Duration + Play Button */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography fontWeight={500}>
                              {msg.duration} sec
                            </Typography>

                            {isLoading ? (
                              <CircularProgress size={18} />
                            ) : isPlaying ? (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={handleCloseAudio}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => handlePlayAudio(msg)}
                              >
                                <PlayCircleFilledIcon fontSize="small" />
                              </IconButton>
                            )}

                            {deletingRowId === msg.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteAudio(msg)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expand Audio Player */}
                      {isPlaying &&
                        playingAudioData?.messageId === msg.id && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <audio
                                controls
                                src={playingAudioData.url}
                                autoPlay
                                onPause={handleCloseAudio}
                                onEnded={handleCloseAudio}
                                style={{ width: "100%" }}
                              >
                                Your browser does not support the audio element.
                              </audio>
                            </TableCell>
                          </TableRow>
                        )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={localMessages.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Paper>
  );
};

export default MailboxDetails;